import Vue from 'vue'
import ApiService from '@/common/api.service'

export const state = {
  /*
   * zoneSelectionSets is a "grouping" of multiple zones that are
   * selected. For example, one might wish to have a "global" set of
   * selected zones that is persistent across all screens or one
   * might want to create a set of selected zones for a particular
   * individual/group of widgets.
   */
  zoneSelectionSets: {},
  queryByClient: null,
  queryMode: null // Can be: 'client_zones', 'global_zones' or null
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @param state Object
   *
   * @return Object
   */
  get (state) {
    return state
  },

  /**
   * Returns the sets of selected zones.
   *
   * @param state Object
   *
   * @return Object
   */
  zoneSelectionSets (state) {
    return state.zoneSelectionSets
  },

  /**
   * Returns either a client object or null depending on whether
   * queries should be made by client.
   *
   * @param state
   *
   * @return {Object|null}
   */
  getClient (state) {
    return state.queryByClient
  },

  /**
   * Returns 'client_zones' or 'global_zones' or null
   *
   * @param state
   *
   * @returns String|null
   */
  getQueryMode (state) {
    return state.queryMode
  }
}

export const actions = {
  /**
   * Query/find one or more zones.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    const queryMode = context.getters['getQueryMode']
    if (queryMode === 'client_zones') {
      const queryByClient = context.getters['getClient']
      if (queryByClient !== null) {
        return ApiService
          .get(`geo/client/zones/by-client/${queryByClient.id}`, params)
          .then((response) => {
            return response
          })
      } else {
        return new Promise((resolve) => {
          resolve()
        })
      }
    } else if (queryMode === 'global_zones') {
      return ApiService
        .get(`geo/global/zones`, params)
        .then((response) => {
          return response
        })
    } else {
      return new Promise((resolve) => {
        resolve()
      })
    }
  }
}

export const mutations = {
  /**
   * Add a map or array of object zones that have been selected within
   * the specified set.
   *
   * @param state    Object
   * @param set      String
   * @param vehicles Array|Map
   */
  addSelectedZones (state, { set, zones }) {
    if (!state.zoneSelectionSets.hasOwnProperty(set)) {
      Vue.set(state.zoneSelectionSets, set, {})
    }

    zones.forEach(zone => {
      Vue.set(state.zoneSelectionSets[set], zone.id, zone)
    })
  },

  /**
   * Removes an array of zones from the set's list of selected zones.
   * The array must consist of the id's of the zones that are no
   * longer selected.
   *
   * @param state    Object
   * @param set      String
   * @param zones    Array
   */
  removeSelectedZones (state, { set, zones }) {
    if (state.zoneSelectionSets.hasOwnProperty(set)) {
      zones.forEach(zone => {
        Vue.delete(state.zoneSelectionSets[set], zone.id)
      })
    }
  },

  /**
   * Clears all selected zones.
   *
   * @param state  Object
   */
  clearAllSelectedZones (state) {
    Vue.set(state, 'zoneSelectionSets', {})
  },

  /**
   * Clears all selected zones within a specified set.
   *
   * @param state  Object
   * @param set      String
   */
  clearSelectedZones (state, set) {
    if (state.zoneSelectionSets.hasOwnProperty(set)) {
      Vue.set(state.zoneSelectionSets, set, {})
    }
  },

  /**
   * Purge the module's state.
   *
   * @param state Object
   */
  purge (state) {
    state.zoneSelectionSets = {}
    state.queryByClient = null
  },

  /**
   * Sets the client for which the zones should be queried.
   *
   * @param state
   *
   * @param client Object|null
   */
  setClient (state, client) {
    state.queryByClient = client
  },

  /**
   * Set the query mode. Supported modes are 'client_zones' and 'global_zones'.
   *
   * @param state
   * @param queryMode
   */
  setQueryMode (state, queryMode) {
    state.queryMode = queryMode
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/zones/findZonesWidgetModule.js